import { useCallback, useEffect, useRef, useState } from "react";
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));
}
interface UseCanvasZoomOptions {
  /** The element that receives wheel/touch events (the scroll container) */
  containerRef: React.RefObject<HTMLElement | null>;
  /** The element being scaled (the canvas) */
  canvasRef: React.RefObject<HTMLElement | null>;
}
interface UseCanvasZoomReturn {
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  /** Apply as style to the canvas element */
  canvasStyle: React.CSSProperties;
}
export function useCanvasZoom({
  containerRef,
  canvasRef
}: UseCanvasZoomOptions): UseCanvasZoomReturn {
  const [zoom, setZoom] = useState(1);
  // Transform origin in the canvas's natural (unscaled) pixel coordinates.
  const [origin, setOrigin] = useState<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0
  });

  // Refs so handlers don't need to be re-bound on zoom change.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const originRef = useRef(origin);
  originRef.current = origin;
  const pinchDistRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef(1);
  const lastPointerRef = useRef<{
    x: number;
    y: number;
  } | null>(null);

  /**
   * Apply a new zoom level anchored at a specific viewport point.
   * Sets transform-origin to the cursor's natural canvas coordinate and
   * adjusts scroll so the same content stays under the cursor.
   */
  const applyZoomAt = useCallback((nextZoom: number, clientX: number, clientY: number) => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const clamped = clampZoom(nextZoom);
    if (clamped === zoomRef.current) return;
    if (!container || !canvas) {
      setZoom(clamped);
      return;
    }
    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    if (canvasRect.width === 0 || canvasRect.height === 0) {
      setZoom(clamped);
      return;
    }

    // Natural (unscaled) canvas coords of the cursor.
    const naturalX = (clientX - canvasRect.left) / zoomRef.current;
    const naturalY = (clientY - canvasRect.top) / zoomRef.current;

    // With transform-origin at (naturalX, naturalY), that point stays at
    // its untransformed layout position regardless of scale. We want that
    // position to land under the cursor, so:
    //   containerRect.left + canvas.offsetLeft + naturalX - scrollLeft = clientX
    const targetScrollLeft = containerRect.left + canvas.offsetLeft + naturalX - clientX;
    const targetScrollTop = containerRect.top + canvas.offsetTop + naturalY - clientY;
    setOrigin({
      x: naturalX,
      y: naturalY
    });
    setZoom(clamped);

    // Sync scroll after the new transform renders.
    requestAnimationFrame(() => {
      container.scrollLeft = Math.max(0, targetScrollLeft);
      container.scrollTop = Math.max(0, targetScrollTop);
    });
  }, [containerRef, canvasRef]);

  /**
   * Zoom by a delta, anchored at the last known pointer position if it's
   * over the container, otherwise at the container center.
   */
  const zoomByDelta = useCallback((delta: number) => {
    const container_0 = containerRef.current;
    if (!container_0) {
      setZoom(z => clampZoom(z + delta));
      return;
    }
    const rect = container_0.getBoundingClientRect();
    const pointer = lastPointerRef.current;
    const isPointerInside = pointer && pointer.x >= rect.left && pointer.x <= rect.right && pointer.y >= rect.top && pointer.y <= rect.bottom;
    const anchor = isPointerInside ? pointer : {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
    applyZoomAt(zoomRef.current + delta, anchor.x, anchor.y);
  }, [applyZoomAt, containerRef]);
  const zoomIn = useCallback(() => zoomByDelta(ZOOM_STEP), [zoomByDelta]);
  const zoomOut = useCallback(() => zoomByDelta(-ZOOM_STEP), [zoomByDelta]);
  const resetZoom = useCallback(() => {
    setZoom(1);
    setOrigin({
      x: 0,
      y: 0
    });
  }, []);

  // Track pointer position so button-based zoom can anchor at the cursor.
  useEffect(() => {
    const container_1 = containerRef.current;
    if (!container_1) return;
    const handler = (e: PointerEvent) => {
      lastPointerRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };
    container_1.addEventListener("pointermove", handler);
    return () => container_1.removeEventListener("pointermove", handler);
  }, [containerRef]);

  // Ctrl+Wheel zoom (also handles macOS trackpad pinch, which the browser
  // reports as wheel events with ctrlKey: true and small fractional deltaY).
  useEffect(() => {
    const container_2 = containerRef.current;
    if (!container_2) return;
    const handleWheel = (e_0: WheelEvent) => {
      if (!e_0.ctrlKey && !e_0.metaKey) return;
      e_0.preventDefault();

      // Trackpad pinch sends small fractional deltaY (~0.5–3).
      // Mouse wheel sends larger values (~100). Normalize both to a
      // reasonable zoom delta by clamping the raw value.
      const raw = -e_0.deltaY;
      const delta_0 = Math.sign(raw) * Math.min(Math.abs(raw), 10) * 0.01;
      applyZoomAt(zoomRef.current + delta_0, e_0.clientX, e_0.clientY);
    };
    container_2.addEventListener("wheel", handleWheel, {
      passive: false
    });
    return () => container_2.removeEventListener("wheel", handleWheel);
  }, [containerRef, applyZoomAt]);

  // Ctrl+Plus / Ctrl+Minus / Ctrl+0 keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e_1: KeyboardEvent) => {
      if (!e_1.ctrlKey && !e_1.metaKey) return;
      if (e_1.key === "=" || e_1.key === "+") {
        e_1.preventDefault();
        zoomByDelta(ZOOM_STEP);
      } else if (e_1.key === "-") {
        e_1.preventDefault();
        zoomByDelta(-ZOOM_STEP);
      } else if (e_1.key === "0") {
        e_1.preventDefault();
        setZoom(1);
        setOrigin({
          x: 0,
          y: 0
        });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomByDelta]);

  // Pinch-to-zoom on touch devices (anchored at midpoint of the two touches).
  useEffect(() => {
    const container_3 = containerRef.current;
    if (!container_3) return;
    function getPinchData(touches: TouchList) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return {
        distance: Math.hypot(dx, dy),
        centerX: (touches[0].clientX + touches[1].clientX) / 2,
        centerY: (touches[0].clientY + touches[1].clientY) / 2
      };
    }
    const handleTouchStart = (e_2: TouchEvent) => {
      if (e_2.touches.length === 2) {
        const data = getPinchData(e_2.touches);
        pinchDistRef.current = data.distance;
        pinchStartZoomRef.current = zoomRef.current;
      }
    };
    const handleTouchMove = (e_3: TouchEvent) => {
      if (e_3.touches.length !== 2 || pinchDistRef.current === null) return;
      e_3.preventDefault();
      const data_0 = getPinchData(e_3.touches);
      const scale = data_0.distance / pinchDistRef.current;
      applyZoomAt(pinchStartZoomRef.current * scale, data_0.centerX, data_0.centerY);
    };
    const handleTouchEnd = () => {
      pinchDistRef.current = null;
    };
    container_3.addEventListener("touchstart", handleTouchStart, {
      passive: true
    });
    container_3.addEventListener("touchmove", handleTouchMove, {
      passive: false
    });
    container_3.addEventListener("touchend", handleTouchEnd, {
      passive: true
    });
    return () => {
      container_3.removeEventListener("touchstart", handleTouchStart);
      container_3.removeEventListener("touchmove", handleTouchMove);
      container_3.removeEventListener("touchend", handleTouchEnd);
    };
  }, [containerRef, applyZoomAt]);
  const canvasStyle: React.CSSProperties = {
    transform: zoom === 1 ? undefined : `scale(${zoom})`,
    transformOrigin: `${origin.x}px ${origin.y}px`
  };
  return {
    zoom,
    zoomIn,
    zoomOut,
    resetZoom,
    canvasStyle
  };
}